import {
  styled,
  TableCell,
  Box,
  Typography,
  Toolbar,
  OutlinedInput,
  Tab,
} from "@mui/material";
import moment from "moment/moment";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import {
  calculateFill,
  getBroker,
  getCommission,
  getCustodian,
  getCustodianFromTradeObj,
  getFund,
  getFundFromTradeObj,
  getRouter,
  getStrategy,
  getStrategyFromTradeObj,
  hashTicker,
} from "./mainFunctions";
import { sendWebSocketMessage } from "../../hooks/useWebSocket";
import { findLivePriceForTicker } from "../../utils/livePriceTickerMatch";
import { formatTimeAgo } from "../../utils/helpers";
import { isIgnoredMonitorStrategy } from "../../utils/ignoredMonitorStrategies";

export const getParentAccountName = (accounts, custodianId) => {
  for (const accountName in accounts) {
    if (accounts[accountName].custodian === custodianId) {
      return accountName;
    }
  }
  return null;
};
export const processFundsWithLotsAndStrategies = (lots, funds, strategies) => {
  const findFundById = (fundId) => funds.find((fund) => fund.fundId === fundId);
  const findStrategyById = (strategyId) =>
    strategies.find((strategy) => strategy.strategyId === strategyId);

  const result = [];

  lots.forEach((lot) => {
    const fund = findFundById(lot.fund);
    const strategy = lot.strategy ? findStrategyById(lot.strategy) : null;

    let fundInResult = result.find((item) => item.fundId === fund.fundId);

    if (!fundInResult) {
      fundInResult = {
        fundId: fund?.fundId,
        abbreviation: fund?.abbreviation,
        name: fund?.name,
        strategies: [],
      };
      result.push(fundInResult);
    }

    if (strategy) {
      const strategyInResult = fundInResult.strategies.find(
        (item) => item.id === strategy.strategyId
      );
      if (!strategyInResult) {
        fundInResult.strategies.push({
          id: strategy.strategyId,
          name: strategy.name,
        });
      }
    }
  });

  return result;
};
export const findBrokerId = (abbreviation, brokers) => {
  const broker = brokers?.find((i) => {
    return i?.abbreviation == abbreviation;
  });
  return broker?.brokerId;
};
export const findBrokerAbb = (id, brokers) => {
  const broker = brokers?.find((i) => {
    return i?.brokerId == id;
  });
  return broker?.abbreviation;
};

export const findStrategy = (id, strategies) => {
  const strategy = strategies?.find((i) => {
    return i?.strategyId == id;
  });
  return strategy?.name;
};
export const findStrategyById = (name, strategies) => {
  const strategy = strategies?.find((i) => {
    return i?.name == name;
  });
  return strategy?.strategyId;
};
export const findFundById = (abbreviation, funds) => {
  const fund = funds?.find((i) => {
    return i?.abbreviation == abbreviation;
  });
  return fund?.fundId;
};
export const findFundAbbById = (id, funds) => {
  const fund = funds?.find((i) => {
    return i?.fundId == id;
  });
  return fund?.abbreviation;
};
export const findFundByName = (name, funds) => {
  const fund = funds?.find((i) => {
    return i?.name == name;
  });
  return fund?.fundId;
};
export const findFundAbbByName = (name, funds) => {
  const fund = funds?.find((i) => {
    return i?.name == name;
  });
  return fund?.abbreviation;
};
export const findFund = (id, funds) => {
  const fund = funds?.find((i) => {
    return i?.fundId == id;
  });
  return fund?.name;
};
export const findCust = (id, custodians) => {
  const custodian = custodians?.find((i) => {
    return i?.custodianId == id;
  });
  return custodian?.name;
};
export const findCustId = (name, custodians) => {
  const custodian = custodians?.find((i) => {
    return i?.abbreviation == name;
  });
  return custodian?.custodianId;
};
export const findCustByName = (name, custodians) => {
  const custodian = custodians?.find((i) => {
    return i?.name == name;
  });
  return custodian?.custodianId;
};

//format number
export const calculateAndFormat = (value) => {
  if (value && value != "") {
    value = parseFloat(value);
    const roundedValue = Math.round(value);
    return roundedValue.toLocaleString();
  } else return value;
};
export const calculateAndFormatNoAbsInt = (value) => {
  if (value !== "" && !isNaN(value)) {
    const integerValue = parseInt(value);
    return integerValue.toLocaleString(undefined, { minimumFractionDigits: 0 });
  } else {
    return value;
  }
};

export const calculateAndFormatNoAbs = (value) => {
  if (value && value !== "") {
    const numericValue = Math.floor(parseFloat(value));
    return numericValue.toLocaleString();
  } else {
    return value;
  }
};

export const formatValue = (value, nodecimal = false) => {
  if (value && value != "") {
    const parsedValue = Math.abs(parseFloat(value));

    if (value === 0) {
      return "0.00";
    }
    if (parsedValue >= 1000) {
      if (nodecimal) {
        return parsedValue.toLocaleString("en-US", {
          style: "decimal",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });
      } else {
        return parsedValue.toLocaleString("en-US", {
          style: "decimal",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      }
    }
    if (nodecimal) {
      return parsedValue.toFixed(0);
    } else {
      return parsedValue.toFixed(2);
    }
  } else return value;
};

export const oneDecimal = (value) => {
  if (value && value != "") {
    const parsedValue = parseFloat(value);
    return parsedValue.toFixed(1);
  } else return value;
};
export const twoDecimal = (value) => {
  if (value && value != "") {
    const parsedValue = parseFloat(value);
    return parsedValue.toFixed(2);
  } else return value;
};
export const fourDecimal = (value) => {
  if (value && value != "") {
    const parsedValue = parseFloat(value);
    return parsedValue.toFixed(4);
  } else return value;
};
export const formatValueDecimal = (value) => {
  if (value && value != "") {
    const parsedValue = parseFloat(value);

    if (value == 0) {
      return "0.00";
    }
    if (parsedValue >= 1000) {
      return parsedValue.toLocaleString("en-US", {
        style: "decimal",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
    return parsedValue.toFixed(2);
  } else return value;
};

//find strategy
export const findName = (key, strategies) => {
  if (key && strategies) {
    const findName = strategies?.find((i) => {
      return i.strategyId == key;
    });
    return findName?.name;
  } else {
    return key;
  }
};
export const findAum = (key, funds) => {
  if (key && funds) {
    const findName = funds?.find((i) => {
      return i.fund == key;
    });
    return findName?.aum;
  } else {
    return key;
  }
};

//Trades Data
export const tradesData = (
  trades,
  funds,
  brokers,
  routeList,
  strategies,
  custodians,
  livePrices,
  prioritySwitch
) => {
  const SimpleArray = [];

  trades?.forEach((tradeObj, i) => {
    const simplerObject = {};
    const tradeId = tradeObj.tradeId;
    const lastOrder = tradeObj.orders.slice(-1)[0];
    const tradeObject = tradeObj;
    const index = i;

    if (lastOrder) {
      const updatedDistribute = Object.entries(
        lastOrder?.distribution || {}
      ).reduce((acc, [key, value]) => {
        const [account, fund, _] = key.split("#");
        const strategySplit = key.split("#0#");
        const strategy = strategySplit.length > 1 ? strategySplit[1] : "";

        if (!acc[fund]) {
          acc[fund] = [];
        }

        acc[fund].push({
          shareCount: Number(value),
          account: account,
          strategy: findStrategy(strategy, strategies),
          child: "C",
          fund: findFundAbbById(fund, funds),
          broker: getBroker(brokers, lastOrder),
        });

        return acc;
      }, {});

      const distribute = calculateFill(updatedDistribute, tradeObj);
      const fundStatus = Object.keys(distribute)?.length > 0 ? true : false;
      const tradeDate = lastOrder?.date;
      const process = lastOrder?.process;
      const tradeType =
        hashAction(lastOrder?.action) + hashPosition(lastOrder?.position);

      const totalShareCount = Object.keys(distribute).reduce((sum, key) => {
        return (
          sum +
          distribute[key].reduce((acc, entry) => acc + entry.shareCount, 0)
        );
      }, 0);
      const firstDistributionKey = Object.keys(distribute)?.[0];
      const firstDistribution = distribute[firstDistributionKey];
      if (Object.keys(distribute).length > 0) {
        const allDistributions = Object.values(distribute).flat();
        simplerObject["distributions"] = allDistributions.map(
          (distributionObj, i) => ({
            ...distributionObj,
            id: i + 1,
          })
        );
        simplerObject["parent"] = allDistributions.length > 1 ? "P" : "S";
      } else {
        simplerObject["distributions"] = [];
        simplerObject["parent"] = "S";
      }

      const tradeAmount = totalShareCount.toFixed(1);
      const sec = "";
      const occ = "";
      const orf = "";

      let working = "";
      let tradeFills = "";
      let completed = "";
      let price = " ";
      let PriceManual = false;

      if (tradeObj.manuals && tradeObj.manuals.length > 0) {
        working = ManualWorkingShares(totalShareCount, tradeObj.manuals);
        tradeFills = "M";
        completed = ManualShareCount(tradeObj.manuals);
        price = ManualSharePrice(tradeObj.manuals);
        PriceManual = true;
      } else if (tradeObj.fill && Object.keys(tradeObj.fill).length > 0) {
        working = FillWorkingShares(totalShareCount, tradeObj.fill);
        tradeFills = "A";
        completed = FillShareCount(tradeObj.fill);
        price = FillSharePrice(tradeObj.fill);
        PriceManual = false;
      } else if (
        tradeObj?.orders?.length > 0 &&
        tradeObj?.orders[tradeObj?.orders?.length - 1]?.broker ==
        "5b676cc69b3fe572df5032c52b8496ef"
      ) {
        // Find matching market data         by ticker symbol
        let matchedPrice = 0;
        if (livePrices && livePrices?.length > 0 && lastOrder?.ticker?.symbol) {
          const matchingMarketData = findLivePriceForTicker(
            livePrices,
            lastOrder.ticker
          );
          if (matchingMarketData?.price) {
            matchedPrice = matchingMarketData?.price;
          }
        }

        working = 0; // Working shares = 0
        tradeFills = ""; // Trade fills = empty string
        completed = totalShareCount?.toFixed(4); // Completed = total shares
        price = matchedPrice; // Price from matched market data
        PriceManual = false; // Not manual price
      } else {
        working = totalShareCount?.toFixed(4);
        tradeFills = "";
        completed = 0;
        price = 0;
        PriceManual = false;
      }

      simplerObject["brokerId"] = getBroker(brokers, lastOrder);
      simplerObject["comm"] = getCommission(tradeObj);
      simplerObject["oms"] = getRouter(routeList, tradeObj);
      simplerObject["prime"] = getCustodian(
        funds,
        lastOrder,
        custodians,
        distribute
      );
      simplerObject["client"] = getFund(funds, distribute);
      simplerObject["strategy"] = getStrategy(strategies, distribute);
      simplerObject["index"] = index;
      simplerObject["process"] = process;
      simplerObject["tradeObject"] = tradeObject;
      simplerObject["fundStatus"] = fundStatus;
      simplerObject["TradeTime"] = tradeDate;
      simplerObject["TradeType"] = tradeType;
      simplerObject["Pcm"] = hashTicker(lastOrder?.ticker, TradeInvestment);
      simplerObject["ordered"] = Number(tradeAmount);
      simplerObject["working"] = Number(working);
      simplerObject["tradefills"] = tradeFills;
      simplerObject["completed"] = Number(completed);
      simplerObject["price"] = Number(price);
      simplerObject["PriceManual"] = PriceManual;
      simplerObject["sec"] = sec;
      simplerObject["occ"] = occ;
      simplerObject["orf"] = orf;
      simplerObject["Ins"] = lastOrder?.instructions;
      simplerObject["ordertype"] = hashType(lastOrder?.type);
      simplerObject["routing"] = lastOrder.router != undefined ? "Yes" : "No";
      simplerObject["currency"] = hashCurrencyEnum(lastOrder?.ticker?.currency);
      simplerObject["InvestmentType"] = hashInvestment(
        lastOrder?.ticker?.investment
      );
      simplerObject["PositionType"] = hashPosition(lastOrder?.position);
      simplerObject["expiration"] = lastOrder?.ticker?.expirationDate
        ? moment(lastOrder?.ticker?.expirationDate)?.format("MMM/DD/YY")
        : " ";
      simplerObject["strikeprice"] = lastOrder?.ticker?.strikePrice
        ? Number(lastOrder?.ticker?.strikePrice)
        : " ";
      simplerObject["putsorcall"] = hashOption(lastOrder?.ticker?.option);
      simplerObject["tradeId"] = tradeId;

      SimpleArray.push(simplerObject);
    }
  });

  return SimpleArray;
};

export function clean(obj) {
  for (var propName in obj) {
    if (
      obj[propName] === null ||
      obj[propName] === undefined ||
      obj[propName] === ""
    ) {
      delete obj[propName];
    }
  }
  return obj;
}

export function applySortFilter(array, comparator, query) {
  if (array && array.length > 0) {
    let filteredArray = array;

    if (query) {
      filteredArray = array.filter((_) => {
        const queryLower = query.toLowerCase();
        return (
          _?.name?.toLowerCase()?.includes(queryLower) ||
          _?.fundName?.toLowerCase()?.includes(queryLower) ||
          _?.strategyName?.toLowerCase()?.includes(queryLower) ||
          _?.abbreviation?.toLowerCase()?.includes(queryLower) ||
          _?.tickerSymbol?.toLowerCase()?.includes(queryLower) ||
          _?.custodianName?.toLowerCase()?.includes(queryLower) ||
          _?.positionType?.toLowerCase()?.includes(queryLower) ||
          String(_?.shares || "")?.includes(queryLower) ||
          String(_?.price || "")?.includes(queryLower) ||
          String(_?.averageCostPerShare || "")?.includes(queryLower) ||
          _?.primaryRouterName?.toLowerCase()?.includes(queryLower) ||
          (_?.date &&
            moment(_?.date)
              .format("MMM DD, YYYY")
              .toLowerCase()
              .includes(queryLower)) ||
          (_?.date &&
            moment(_?.date)
              .format("HH:mm A")
              .toLowerCase()
              .includes(queryLower))
        );
      });
    }

    // Then apply sorting
    const stabilizedThis = filteredArray?.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });

    return stabilizedThis?.map((el) => el[0]);
  } else {
    return [];
  }
}

function mixedTypeComparator(a, b, orderBy) {
  const aValue = a[orderBy] || "";
  const bValue = b[orderBy] || "";

  if (aValue === "" && bValue === "") return 0;
  if (aValue === "") return 1;
  if (bValue === "") return -1;

  const parsedA = isNaN(parseFloat(aValue)) ? aValue : parseFloat(aValue);
  const parsedB = isNaN(parseFloat(bValue)) ? bValue : parseFloat(bValue);

  if (typeof parsedA === "number" && typeof parsedB === "number") {
    return parsedB - parsedA;
  }

  if (typeof parsedA === "number" && typeof parsedB !== "number") {
    return -1;
  }
  if (typeof parsedA !== "number" && typeof parsedB === "number") {
    return 1;
  }

  const lowerA = typeof parsedA === "string" ? parsedA.toLowerCase() : parsedA;
  const lowerB = typeof parsedB === "string" ? parsedB.toLowerCase() : parsedB;

  if (lowerB < lowerA) {
    return -1;
  }
  if (lowerB > lowerA) {
    return 1;
  }
  return 0;
}
export function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => mixedTypeComparator(a, b, orderBy)
    : (a, b) => -mixedTypeComparator(a, b, orderBy);
}
export const checkStringType = (inputString) => {
  if (/^[a-zA-Z0-9]+$/.test(inputString)) {
    return 1;
  }
  if (/^[a-zA-Z ]+[.\/ ][a-zA-Z0-9 ]+$/.test(inputString)) {
    return 2;
  }

  return 3;
};

export const extractTickerAndWeight = (entry) => {
  const match = entry.match(/(.+?)\s*([+-]?\d+(\.\d+)?)\s*%?\s*$/);
  if (!match) {
    return null;
  }

  let value = parseFloat(match[2]);

  if (!entry.includes("%")) {
    value *= 100;
  }

  const formattedValue = parseFloat(value).toFixed(4);

  let name = match[1].trim();
  const { ticker, isValid } = inputChecker(name);
  const { symbol, country, currency, investment, stock } = ticker;
  const stockProps = stock ? { stock } : {};

  return {
    symbol: symbol,
    value: formattedValue,
    name: name?.toUpperCase() || "",
    country: country,
    investment: investment,
    currency: currency,
    invalid: !isValid,
    ...stockProps,
  };
};
export const ManualWorkingShares = (totalShareCount, manuals) => {
  return (totalShareCount - manuals?.slice(-1)[0]?.shareCount)?.toFixed(4);
};
export const FillWorkingShares = (totalShareCount, fill) => {
  return (totalShareCount - fill?.shareCount)?.toFixed(4);
};

export const ManualShareCount = (manuals) => {
  return manuals?.slice(-1)[0]?.shareCount?.toFixed(4);
};
export const FillShareCount = (fill) => {
  return fill?.shareCount?.toFixed(4);
};
export const ManualSharePrice = (manuals) => {
  return manuals?.slice(-1)[0]?.sharePrice?.toFixed(4);
};
export const FillSharePrice = (fill) => {
  return fill?.sharePrice?.toFixed(4);
};
// export const ManualCommission = (manuals) => {
//   return (
//     manuals?.slice(-1)[0]?.commission / manuals?.slice(-1)[0]?.shareCount
//   )?.toFixed(4);
// };
// export const FillCommission = (fill) => {
//   if (fill.commission && fill.commission != undefined)
//     return (fill?.commission / fill?.shareCount)?.toFixed(4);
//   else return 0;
// };
export const criteriaOptions = [
  { name: "Not Allowed", value: "NotAllowed" },
  { name: "Allocation", value: "Allocation" },
  { name: "Market Cap", value: "MarketCap" },
  { name: "Price", value: "Price" },
  { name: "Shares", value: "Shares" },
  { name: "Sectors", value: "Sectors" },
  { name: "Assets Type", value: "Assets" },
  { name: "Volume", value: "Volume" },
];
export const criteriaOptionsVal = [
  { name: "Not Allowed", value: "NotAllowed" },
  { name: "Allocation", value: "Allocation" },
  { name: "Market Cap", value: "MarketCap" },
  { name: "Price", value: "Price" },
  { name: "Shares", value: "Shares" },
  { name: "Sectors", value: "Sectors" },
  { name: "Assets", value: "Assets" },
  { name: "Volume", value: "Volume" },
];
export const operationOptions = [">", "<", "="];
export const unitOptions = ["$", "%"];
export const priorityTrades = ["Auto", "Manual", "All"];
export const EnumpriorityTrades = { Auto: 0, Manual: 1, All: 2 };
export const Positions = ["Long", "Short"];
export const EnumPosition = { Long: 0, Short: 1 };
export const ReportType = ["FundCount", "Goldman", "Schwab", "TopWater"];
export const ReportTypeEnum = {
  FundCount: 0,
  Goldman: 1,
  Schwab: 2,
  TopWater: 3,
};

export const hashGetReportType = (report) => {
  const lowerReport = report?.toLowerCase();

  switch (lowerReport) {
    case "fundcount":
      return ReportTypeEnum.FundCount;
    case "goldman":
      return ReportTypeEnum.Goldman;
    case "topwater":
      return ReportTypeEnum.TopWater;
    case "schwab":
      return ReportTypeEnum.Schwab;
    default:
      return null;
  }
};
export const BlacklistTypeEnum = {
  NotAllowed: 0,
  Allocation: 1,
  MarketCap: 2,
  Price: 3,
  Shares: 4,
  Sectors: 5,
  Assets: 6,
  Volume: 7,
};
export const hashGetBlacklistType = (criteria) => {
  const lowerCriteria = criteria?.toLowerCase();

  switch (lowerCriteria) {
    case "notallowed":
      return BlacklistTypeEnum.NotAllowed;
    case "allocation":
      return BlacklistTypeEnum.Allocation;
    case "marketcap":
      return BlacklistTypeEnum.MarketCap;
    case "price":
      return BlacklistTypeEnum.Price;
    case "shares":
      return BlacklistTypeEnum.Shares;
    case "sectors":
      return BlacklistTypeEnum.Sectors;
    case "assets":
      return BlacklistTypeEnum.Assets;
    case "volume":
      return BlacklistTypeEnum.Volume;
    default:
      return null;
  }
};

export const hashGetBlacklistCritera = (criteria) => {
  const lowerCriteria = Number(criteria);

  switch (lowerCriteria) {
    case 0:
      return "NotAllowed";
    case 1:
      return "Allocation";
    case 2:
      return "MarketCap";
    case 3:
      return "Price";
    case 4:
      return "Shares";
    case 5:
      return "Sectors";
    case 6:
      return "Assets";
    case 7:
      return "Volume";
    default:
      return null;
  }
};
export const hashAction = (val) => {
  switch (val) {
    case 0:
      return "Buy";
    case 1:
      return "Exercised";
    case 2:
      return "Expired";
    case 3:
      return "Sell";
    default:
      return null;
  }
};

export const hashPosition = (val) => {
  switch (val) {
    case 0:
      return "Long";
    case 1:
      return "Short";
    default:
      return null;
  }
};

export const hashInvestment = (val) => {
  switch (val) {
    case 0:
      return "Bond";
    case 1:
      return "Commodity";
    case 2:
      return "Crypto";
    case 3:
      return "Currency";
    case 4:
      return "Equity";
    case 5:
      return "Future";
    case 6:
      return "Government";
    case 7:
      return "Index";
    case 8:
      return "MoneyMarket";
    case 9:
      return "MutualFund";
    case 10:
      return "Option";
    case 11:
      return "Partnership";
    case 12:
      return "Preferred";
    case 13:
      return "Private";
    case 14:
      return "Right";
    case 15:
      return "Swap";
    case 16:
      return "Unknown";
    case 17:
      return "Warrant";
    default:
      return null;
  }
};
export const hashInvestmentReverse = (str) => {
  switch (str.toLowerCase()) {
    case "bond":
      return 0;
    case "commodity":
      return 1;
    case "crypto":
      return 2;
    case "currency":
      return 3;
    case "equity":
      return 4;
    case "future":
      return 5;
    case "government":
      return 6;
    case "index":
      return 7;
    case "moneymarket":
      return 8;
    case "mutualfund":
      return 9;
    case "option":
      return 10;
    case "partnership":
      return 11;
    case "preferred":
      return 12;
    case "private":
      return 13;
    case "right":
      return 14;
    case "swap":
      return 15;
    case "unknown":
      return 16;
    case "warrant":
      return 17;
    default:
      return null;
  }
};

export const hashTradeType = (action, position) => {
  if (action == 0 && position == 0) {
    return 0;
  } else if (action == 3 && position == 0) {
    return 1;
  } else if (action == 3 && position == 1) {
    return 2;
  } else if (action == 0 && position == 1) {
    return 3;
  }
};
export const hashType = (val) => {
  switch (val) {
    case 0:
      return "Limit";
    case 1:
      return "Market";
    case 2:
      return "MarketOnClose";
    case 3:
      return "Stop";
    case 4:
      return "VWAP";
    default:
      return null;
  }
};
export const hashOption = (val) => {
  switch (val) {
    case 0:
      return "calls";
    case 1:
      return "puts";
    default:
      return null;
  }
};
export const hashCurrencyEnumString = (str) => {
  switch (str.toLowerCase()) {
    case "aud":
      return 0;
    case "cad":
      return 1;
    case "eur":
      return 2;
    case "gbp":
      return 3;
    case "hkd":
      return 4;
    case "jpy":
      return 5;
    case "nok":
      return 6;
    case "usd":
      return 7;
    case "zar":
      return 8;
    default:
      return null;
  }
};
export const hashCurrencyEnum = (val) => {
  switch (val) {
    case 0:
      return "AUD";
    case 1:
      return "CAD";
    case 2:
      return "EUR";
    case 3:
      return "GBP";
    case 4:
      return "HKD";
    case 5:
      return "JPY";
    case 6:
      return "NOK";
    case 7:
      return "USD";
    case 8:
      return "ZAR";
    default:
      return null;
  }
};
export const hashExchangeEnumReverse = (str) => {
  switch (str.toUpperCase()) {
    case "CN":
      return 0;
    case "FP":
      return 1;
    case "LN":
      return 2;
    case "SJ":
      return 3;
    case "US":
      return 4;
    default:
      return null;
  }
};
export const performance_list = [
  { id: 1, name: "Daily" },
  { id: 2, name: "Weekly" },
  { id: 3, name: "Monthly" },
  { id: 4, name: "Quarterly" },
  { id: 5, name: "Yearly" },
];
export const durationEOD = ["EOD", "GTC", "CUSTOM"];
export const EnumDurationTrades = { EOD: 0, GTC: 1, CUSTOM: 2 };
export const order_Type = ["Limit", "Market", "MarketOnClose", "Stop", "VWAP"];

export const investArr = [
  "Bond",
  "Commodity",
  "Crypto",
  "Currency",
  "Equity",
  "Future",
  "Government",
  "Index",
  "MoneyMarket",
  "MutualFund",
  "Option",
  "Partnership",
  "Preferred",
  "Private",
  "Right",
  "Swap",
  "Unknown",
  "Warrant",
];

export const TradeInvestment = {
  Equity: 4,
  Options: 10,
  MutualFund: 9
};

export const FundCountCols = [
  {
    field: "0",
    headerName: "Fund",
    editable: true,
    width: 130,
  },
  {
    field: "1",
    headerName: "Account #",
    editable: true,
    width: 200,
  },
  {
    field: "2",
    headerName: "Ticker",
    editable: true,
  },
  {
    field: "3",
    headerName: "Description",
    editable: true,
  },
  {
    field: "4",
    headerName: "Broker",
    editable: true,
  },
  {
    field: "5",
    headerName: "Transaction Type",
    editable: true,
    width: 130,
  },
  {
    field: "6",
    headerName: "Trade Date",
    editable: true,
  },
  {
    field: "7",
    headerName: "Settle Date",
    editable: true,
  },
  {
    field: "8",
    headerName: "Quantity",
    editable: true,
  },
  {
    field: "9",
    headerName: "Commission",
    editable: true,
  },
  {
    field: "10",
    headerName: "Price",
    editable: true,
  },
  {
    field: "11",
    headerName: "Gross $",
    editable: true,
  },
  {
    field: "12",
    headerName: "Commission $",
    editable: true,
  },
  {
    field: "13",
    headerName: "Fees",
    editable: true,
  },
  {
    field: "14",
    headerName: "Net $",
    editable: true,
  },
  {
    field: "15",
    headerName: "Instrument Type",
    editable: true,
    width: 130,
  },
  {
    field: "16",
    headerName: "Call/Put",
    editable: true,
  },
  {
    field: "17",
    headerName: "Contract Size",
    editable: true,
  },
  {
    field: "18",
    headerName: "Exp Date",
    editable: true,
  },
  {
    field: "19",
    headerName: "Strike Price",
    editable: true,
  },
  {
    field: "20",
    headerName: "Currency",
    editable: true,
  },
  {
    field: "21",
    headerName: "Transaction Id",
    editable: true,
    width: 200,
  },
  {
    field: "22",
    headerName: "TrdSign",
    editable: true,
  },
  {
    field: "23",
    headerName: "SEC Fee",
    editable: true,
  },
  {
    field: "24",
    headerName: "ORF Fee",
    editable: true,
  },
  {
    field: "25",
    headerName: "OCC Fee",
    editable: true,
  },
  {
    field: "26",
    headerName: "Portfolio",
    editable: true,
  },
  {
    field: "27",
    headerName: "",
    editable: true,
  },
  {
    field: "28",
    headerName: "Custodian",
    editable: true,
  },
];
export const TopwaterTradeCols = [
  {
    field: "0",
    headerName: "TopwaterProductType",
    editable: true,
  },
  {
    field: "1",
    headerName: "Trade Canceled",
    editable: true,
  },
  {
    field: "2",
    headerName: "Transaction Type",
    editable: true,
  },
  {
    field: "3",
    headerName: "Trade Id",
    editable: true,
    width: 200,
  },
  {
    field: "4",
    headerName: "Fund Group Name",
    editable: true,
  },
  {
    field: "5",
    headerName: "Fund Name",
    editable: true,
  },
  {
    field: "6",
    headerName: "Custodian",
    editable: true,
  },
  {
    field: "7",
    headerName: "BB Yellow Key",
    editable: true,
  },
  {
    field: "8",
    headerName: "OCC Code",
    editable: true,
  },
  {
    field: "9",
    headerName: "CUSIP",
    editable: true,
  },
  {
    field: "10",
    headerName: "SEDOL",
    editable: true,
  },
  {
    field: "11",
    headerName: "Trade Date",
    editable: true,
  },
  {
    field: "12",
    headerName: "Settle Date",
    editable: true,
  },
  {
    field: "13",
    headerName: "Quantity",
    editable: true,
  },
  {
    field: "14",
    headerName: "Trade Price",
    editable: true,
  },
  {
    field: "15",
    headerName: "Commissions",
    editable: true,
  },
  {
    field: "16",
    headerName: "SEC Fees",
    editable: true,
  },
  {
    field: "17",
    headerName: "Other Fees",
    editable: true,
  },
  {
    field: "18",
    headerName: "Trade Currency",
    editable: true,
  },
  {
    field: "19",
    headerName: "Settle Currency",
    editable: true,
  },
  {
    field: "20",
    headerName: "Counterparty",
    editable: true,
  },
  {
    field: "21",
    headerName: "Accrued Interest",
    editable: true,
  },
  {
    field: "22",
    headerName: "Trade Time",
    editable: true,
  },
  {
    field: "23",
    headerName: "Currency1",
    editable: true,
  },
  {
    field: "24",
    headerName: "Currency2",
    editable: true,
  },
  {
    field: "25",
    headerName: "Forward Points",
    editable: true,
  },
  {
    field: "26",
    headerName: "Is Financed",
    editable: true,
  },
];

export const TABLE_HEAD_FUNDS_ACCOUNTS = [
  { id: "name", label: "Account Name", alignRight: false },
  { id: "value", label: "Account Number", alignRight: false },
  { id: "action", label: "Action", alignRight: false },
];
export const TABLE_HEAD_FUNDS_CUSTODIANS = [
  { id: "name", label: "Custodian", alignRight: false },
  { id: "value", label: "Account Number", alignRight: false },
  { id: "action", label: "Action", alignRight: false },
];
export const TABLE_HEAD_EQUITY = [
  { id: "Date", label: "Date", alignRight: false },
  { id: "Custodian", label: "Custodian", alignRight: false },
  { id: "Tickers", label: "Ticker", alignRight: false },
  { id: "Sharecount", label: "Share Count", alignRight: false },
  { id: "Cost", label: "Cost Per Share", alignRight: false },
  { id: "Price", label: "Price", alignRight: false },
];
export const TABLE_HEAD_OPTION = [
  { id: "Date", label: "Date", alignRight: false },
  { id: "Custodian", label: "Custodian", alignRight: false },
  { id: "Tickers", label: "Ticker", alignRight: false },
  { id: "Options", label: "Options", alignRight: false },
  { id: "StrikePrice", label: "Strike Price", alignRight: false },
  { id: "Expiry", label: "Expiry", alignRight: false },
  { id: "Contracts", label: "Contracts", alignRight: false },
];
export const TABLE_SINGLE_LINE_TRADE = [
  { id: "fund", label: "Fund", alignRight: true },
  { id: "strategy", label: "Strategy", alignRight: true },
  { id: "action", label: "Action", alignRight: true },
  { id: "ticker", label: "Ticker", alignRight: true },
  { id: "shareCount", label: "Share Count", alignRight: true },

  // { id: "action", label: "Action", alignCenter: true },
];
export const TABLE_CROSS_TRADE = [
  { id: "ignore", label: "Ignore", alignCenter: true, isCheckbox: true },
  { id: "strategy", label: "Strategy", alignRight: true },
  { id: "custodian", label: "Custodian", alignRight: true },
  { id: "broker", label: "Broker", alignRight: true },
  { id: "action", label: "Action", alignRight: true },
  { id: "ticker", label: "Ticker", alignRight: true },
  { id: "shareCount", label: "Share Count", alignRight: true },
  { id: "fund", label: "Fund", alignRight: true },
  // { id: "action", label: "Action", alignCenter: true },
];

export const REBALANCER_SUMMARY = [
  { id: "type", label: "", alignRight: true },
  { id: "marketTrades", label: "Market Trades", alignRight: true },
  { id: "crossingTrades", label: "Crossing Trades", alignRight: true },
  { id: "tradeCount", label: "Total Trades", alignRight: true },
  { id: "marketShares", label: "Market Shares", alignRight: true },
  { id: "crossingShares", label: "Crossing Shares", alignRight: true },
  { id: "shares", label: "Total Shares", alignRight: true },
  { id: "marketDollars", label: "Market Dollars", alignRight: true },
  { id: "crossingDollars", label: "Crossing Dollars", alignRight: true },
  { id: "dollars", label: "Total Dollars", alignRight: true },
];

export const TABLE_CROSS_TRADE_ACTIVITY = [
  { id: "strategy", label: "Strategy", alignRight: true },
  { id: "ticker", label: "Ticker", alignRight: true },
  { id: "position", label: "Activity", alignRight: true },
  { id: "shareCount", label: "Share Count", alignRight: true },
  { id: "broker", label: "Broker", alignRight: true },
  { id: "custodian", label: "Account", alignRight: true },
];
export const TABLE_CROSS_TRADE_RESULTING = [
  { id: "strategy", label: "Strategy", alignRight: true },
  { id: "ticker", label: "Ticker", alignRight: true },
  { id: "activity", label: "Activity", alignRight: true },
  { id: "shares", label: "Share Count", alignRight: true },
  { id: "broker", label: "Broker", alignRight: true },
  { id: "custodian", label: "Account", alignRight: true },
];
export const TABLE_HEAD_TRADE = [
  // { id: "all",  headerName: "All", alignCenter: true, isActive: true },
  {
    field: "Actions",
    headerName: "Actions",
    alignCenter: true,
    isActive: true,
  },
  {
    field: "fundStatus",
    headerName: "Status",
    alignCenter: true,
    isActive: true,
  },
  {
    field: "id",
    headerName: "#",
  },
  {
    field: "TradeTime",
    headerName: "Trade Time",
    alignCenter: true,
    isActive: true,
  },
  {
    field: "TradeType",
    headerName: "Trade Type",
    alignCenter: true,
    isActive: true,
  },
  { field: "Pcm", headerName: "Ticker", alignCenter: true, isActive: true },
  {
    field: "ordered",
    headerName: "Ordered Shares",
    alignCenter: true,
    isActive: true,
  },
  {
    field: "working",
    headerName: "Working Shares",
    alignCenter: true,
    isActive: true,
  },
  {
    field: "tradefills",
    headerName: "A/M",
    alignLeft: true,
    isActive: true,
  },
  {
    field: "completed",
    headerName: "Completed Shares",
    alignLeft: true,
    isActive: true,
  },
  {
    field: "price",
    headerName: "Completion Price Per Share",
    alignLeft: true,
    isActive: true,
  },
  {
    field: "comm",
    headerName: "Comm Per Share",
    alignLeft: true,
    isActive: true,
  },
  {
    field: "sec",
    headerName: "SEC Price per Share",
    alignLeft: true,
    isActive: true,
  },
  {
    field: "occ",
    headerName: "OCC Fee per Share",
    alignLeft: true,
    isActive: true,
  },
  {
    field: "orf",
    headerName: "ORF Fee per Share",
    alignLeft: true,
    isActive: true,
  },
  {
    field: "brokerId",
    headerName: "Broker ID",
    alignLeft: true,
    isActive: true,
  },
  {
    field: "strategy",
    headerName: "Strategy",
    alignLeft: true,
    isActive: true,
  },
  {
    field: "Ins",
    headerName: "Trade Instructions",
    alignLeft: true,
    isActive: true,
  },

  {
    field: "ordertype",
    headerName: "Order type",
    alignLeft: true,
    isActive: true,
  },
  {
    field: "routing",
    headerName: "Order Routed",
    alignLeft: true,
    isActive: true,
  },

  { field: "currency", label: "Currency", alignLeft: true, isActive: true },
  { id: "prime", label: "Prime Broker", alignLeft: true, isActive: true },
  {
    field: "InvestmentType",
    headerName: "Investment Type",
    alignLeft: true,
    isActive: true,
  },
  {
    field: "PositionType",
    headerName: "Position Type",
    alignLeft: true,
    isActive: true,
  },
  { field: "client", headerName: "Client ID", alignLeft: true, isActive: true },

  {
    field: "expiration",
    headerName: "Expiration",
    alignLeft: true,
    isActive: true,
  },
  {
    field: "strikeprice",
    headerName: "Strike Price",
    alignLeft: true,
    isActive: true,
  },
  {
    field: "putsorcall",
    headerName: "Puts or calls",
    alignLeft: true,
    isActive: true,
  },
];
export const TABLE_HEAD_BROKER = [
  { id: "name", label: "Full Name", alignLeft: true },
  { id: "abbreviation", label: "Abbreviation", alignLeft: true },
  { id: "primaryRouterName", label: "Router (EMSX)", alignLeft: true },

  // { id: "Option", label: "Option", alignLeft: true },
  // { id: "routerNames", label: "EMSX (Equity)", alignLeft: true },
  // { id: "emsxop", label: "EMSX (Option)", alignLeft: true },
  // { id: "ibp", label: "IB (Option)", alignLeft: true },
  // { id: "ibeq", label: "IB (Equity)", alignLeft: true },
  { id: "date", label: "Creation Date", alignLeft: true },
  { id: "actions", label: "Actions", alignLeft: true },
];
export const TABLE_HEAD_WATCHLIST = [
  { id: "name", label: "Serial No #", alignLeft: true },
  { id: "stock", label: "Stock (Ticker)", alignLeft: true },
  { id: "ldcp", label: "Last Day Closing Price", alignLeft: true },
  { id: "currentPrice", label: "Current Price ", alignLeft: true },
];

export const TABLE_HEAD_FUND = [
  { id: "abbreviation", label: "Abbreviation", alignLeft: true },
  { id: "name", label: "Full Name", alignLeft: true },
  { id: "SelectedAum", label: "AUM", alignLeft: true },
  { id: "custodiansCount", label: "No Of Custodians", alignLeft: true },
  { id: "accountsCount", label: "No Of Accounts", alignLeft: true },
  { id: "brokerCount", label: "No Of Brokerage", alignLeft: true },
  { id: "creationDate", label: "Creation Date", alignLeft: true },
  { id: "blacklist", label: "Blacklist", alignLeft: true },
  // { id: "actions", label: "Actions", alignLeft: true },
];
export const TABLE_HEAD_MARKET_DATA = [
  { id: "vantage_name", label: "Vantage data item name", alignLeft: true },
  { id: "bloomberg_name", label: "Bloomberg Field", alignLeft: true },
  { id: "position", label: "Position Type", alignLeft: true },
  { id: "investment", label: "Investment Type", alignLeft: true },
  { id: "actions", label: "Actions", alignLeft: true },
];
export const TABLE_HEAD_SIGNALS_LIST = [
  { id: "signal_name", label: "Signal Name", alignLeft: true },
  { id: "submitted_by", label: "Submitted By", alignLeft: true },
  { id: "creationDate", label: "Creation Date", alignLeft: true },
  { id: "actions", label: "Actions", alignLeft: true },
];
export const TABLE_HEAD_SMA = [
  { id: "title", label: "Title", alignLeft: true },
  { id: "account", label: "Account Number", alignLeft: true },
  { id: "strategies", label: "Subscribed Strategies", alignLeft: true },
  { id: "actions", label: "Actions", alignLeft: true },
];
export const TABLE_HEAD_USER = [
  { id: "owner", label: "Email", alignLeft: true },
  { id: "actions", label: "Actions", alignLeft: true },
];
export const TABLE_HEAD_ROLES_MANAGEMENT = [
  { id: "role", label: "Role", alignLeft: true },
  { id: "tradeBlotter", label: "Trade Blotter", alignLeft: true },
  { id: "dailyPerformance", label: "Daily Performance", alignLeft: true },
  {
    id: "performanceDashboard",
    label: "Performance Dashboard",
    alignLeft: true,
  },
  { id: "signals", label: " Signals", alignLeft: true },
  { id: "rebalancer", label: "Rebalancer", alignLeft: true },
  { id: "actions", label: "Actions", alignLeft: true },
];
export const TABLE_HEAD_BOXED_POSITIONS = [
  { id: "date", label: "Date", alignLeft: true },
  { id: "positionType", label: "Position", alignLeft: true },
  { id: "tickerSymbol", label: "Ticker", alignLeft: true },
  { id: "fund", label: "Fund", alignLeft: true },
  { id: "strategy", label: "Strategy", alignLeft: true },
  { id: "custodian", label: "Custodian", alignLeft: true },
  { id: "totalShares", label: "Shares", alignLeft: false },
  { id: "cost", label: "Cost", alignLeft: false },
  { id: "price", label: "Price", alignLeft: false },
];

export const TABLE_HEAD_STRATEGIES = [
  { id: "name", label: "Strategy Name", alignLeft: true },
  { id: "owner", label: "Owner", alignLeft: true },
  { id: "permissions", label: "Permissions", alignLeft: true },
  { id: "date", label: "Last Updated Date", alignLeft: true },
  { id: "desc", label: "Description", alignLeft: true },
  { id: "actions", label: "Actions", alignLeft: true },
];
export const TABLE_HEAD_FUNDALLOCATOR = [
  { id: "name", label: "Strategies", alignCenter: true },
  { id: "existingPercent", label: "Allocation %", alignLeft: true },
  { id: "existingAllocation", label: "Allocation $", alignLeft: true },
  { id: "long", label: "Percent Long", alignLeft: true },
  { id: "short", label: "Percent Short", alignLeft: true },
  { id: "ignoreWhenRebalancing", label: "Use Existing", alignLeft: true },
  { id: "allocation", label: "Allocation $", alignLeft: true },
  { id: "totalAllocation", label: "Allocation %", alignLeft: true },
  { id: "percentLong", label: "Percent Long", alignLeft: true },
  { id: "percentShort", label: "Percent Short", alignLeft: true },
  { id: "broker", label: "Preferred Broker", alignLeft: true },
  { id: "account", label: "Preferred Account", alignLeft: true },
  { id: "byPass", label: "ByPass Grossing", alignLeft: true },
];
export const TABLE_HEAD_TICKER = [
  { id: "creationDate", label: "Creation Date", alignLeft: true },
  { id: "name", label: "Full Name", alignLeft: true },
  { id: "abbreviation", label: "Abbreviation", alignLeft: true },
];
export const TABLE_HEAD_ROUTER = [
  { id: "name", label: "Full Name", alignLeft: true },
  { id: "aum", label: "Aum", alignLeft: true },
  { id: "date", label: "Creation Date", alignLeft: true },
  { id: "actions", label: "Actions", alignLeft: true },
];

export const TABLE_HEAD_CUSTODIAN = [
  { id: "name", label: "Full Name", alignLeft: true },
  { id: "abbreviation", label: "Abbreviation", alignLeft: true },
  { id: "date", label: "Creation Date", alignLeft: true },
  { id: "actions", label: "Actions", alignLeft: true },
];
export const TABLE_HEAD_SECURITY_MASTERS = [
  { id: "check", label: "", alignLeft: true },
  { id: "name", label: "Name", alignLeft: true },
  { id: "tickerSymbol", label: "Ticker", alignLeft: true },
  { id: "cusip", label: "Cusip", alignLeft: true },
  { id: "sedol", label: "Sedol", alignLeft: true },
  { id: "isin", label: "ISIN", alignLeft: true },
  { id: "figi", label: "Figi", alignLeft: true },
  { id: "bloomberg", label: "Bloomberg", alignLeft: true },
  { id: "factset", label: "Factset", alignLeft: true },
];
export const TABLE_HEAD_SECURITY_MASTER_MAPPING = [
  { id: "normalizedIdentifier", label: "String", alignLeft: true },
  { id: "variantIdentifier", label: "Ticker", alignLeft: true },
  { id: "actions", label: "Actions", alignLeft: true },
];
export const TABLE_HEAD_REPORTS = [
  { id: "name", label: "Report Name", alignLeft: true },
  { id: "recentDate", label: "Last Time Generated", alignLeft: true },
  { id: "actions", label: "Actions", alignLeft: true },
];

export const TABLE_HEAD_ARCHIVED_REPORTS = [
  { id: "date", label: "Creation Date", alignLeft: true },
  { id: "date", label: "Generated Time Period", alignLeft: true },
  { id: "stage", label: "Status", alignLeft: true },
  { id: "actions", label: "Actions", alignLeft: true },
];
export const TABLE_HEAD_POSITIONS = [
  { id: "positionType", label: "Position Type", alignLeft: true, width: "11%" },
  { id: "tickerSymbol", label: "Ticker", alignLeft: true, width: "9%" },
  { id: "fundName", label: "Fund", alignLeft: true, width: "14%" },
  { id: "strategyName", label: "Strategy", alignLeft: true, width: "15%" },
  { id: "custodianName", label: "Custodian", alignLeft: true, width: "14%" },
  { id: "shares", label: "Shares", alignLeft: false, width: "100px" },
  {
    id: "averageCostPerShare",
    label: "Avg Cost/Share",
    alignLeft: false,
    width: "108px",
  },
  { id: "price", label: "Price", alignLeft: false, width: "88px" },
  { id: "actions", label: "Actions", alignLeft: true, width: "52px" },
];
export const TABLE_HEAD_PERFORMANCE = [
  { id: "serialNo", label: "Serial No", alignLeft: true },
  { id: "StrategyName", label: "Strategy Name", alignLeft: true },
  { id: "aum", label: "AUM", alignLeft: true },
  { id: "profit", label: "Profit/Loss", alignLeft: true },
];
export const TABLE_HEAD_TRADES_EXECUTED = [
  { id: "Pcm", label: "Ticker", alignLeft: true },
  { id: "TradeType", label: "Trade Type", alignLeft: true },
  { id: "ordered", label: "Shares Ordered", alignLeft: true },
  { id: "comm", label: "Shares Executed", alignLeft: true },
  { id: "brokerId", label: "Broker ID", alignLeft: true },
  { id: "prime", label: "Prime Broker", alignLeft: true },
  { id: "InvestmentType", label: "Investment Type", alignLeft: true },
];
export const TABLE_HEAD_TRADES_EQUITY = [
  { id: "date", label: "Buying Date", alignLeft: true },
  { id: "shareCount", label: "Share Count", alignLeft: true },
  { id: "cost", label: "Cost", alignLeft: true },

  { id: "profitLossMonth", label: "P(L) Monthly", alignLeft: true },
  { id: "profitLossQuarter", label: "P(L) Quarterly", alignLeft: true },
  { id: "profitLossYear", label: "P(L) Yearly", alignLeft: true },
  { id: "profitLossInception", label: "P(L) ITD", alignLeft: true },
  { id: "profitLossTotal", label: "P(L)", alignLeft: true },
  { id: "deltaVal", label: "P(L)%", alignLeft: true },
  { id: "strategy", label: "Strategy", alignLeft: true },
];
export const TABLE_HEAD_TRADES_EQUITY_CLOSEPOS = [
  { id: "ticker", label: "Ticker", alignLeft: true },
  { id: "shareCount", label: "Share Count", alignLeft: true },
  { id: "cost", label: "Cost", alignLeft: true },
  { id: "custodian", label: "Custodian", alignLeft: true },
  { id: "broker", label: "Broker", alignLeft: true },
];
export const TABLE_HEAD_TRADES_OPTIONS = [
  { id: "date", label: "Start Date", alignLeft: true },
  { id: "expirationDate", label: "Expiry Date", alignLeft: true },
  { id: "shareCount", label: "Contract Size", alignLeft: true },
  { id: "strikePrice", label: "Strike Price", alignLeft: true },
  { id: "option", label: "Option Type", alignLeft: true },
  { id: "delta", label: "Delta", alignLeft: true },
  {
    id: "deltaShareCount",
    label: "DA ShareCount",
    alignLeft: true,
  },
  { id: "profitLossWeek", label: "P(L) Weekly", alignLeft: true },
  { id: "profitLossMonth", label: "P(L) Monthly", alignLeft: true },
  { id: "profitLossQuarter", label: "P(L) Quarterly", alignLeft: true },
  { id: "profitLossYear", label: "P(L) Yearly", alignLeft: true },
  { id: "profitLossTotal", label: "P(L)", alignLeft: true },
  { id: "deltaVal", label: "P(L)%", alignLeft: true },
  { id: "strategy", label: "Strategy", alignLeft: true },
];

export const TABLE_HEAD_PORTFOLIO_SHORT = [
  {
    id: "short_size",
    label: "Size",
    title: "Delta Adjusted Percent Of AUM",
    alignRight: true,
  },
  {
    id: "short_deltaadjustedsharecount",
    label: "Shares",
    title: "Delta Adjusted ShareCount",
    alignRight: true,
  },
  {
    id: "short_tickerSymbol",
    label: "Ticker",
    title: "Ticker Symbol",
    alignLeft: true,
  },
  {
    id: "short_last",
    label: "Yest",
    title: "Yesterday's Closing Share Price",
    alignRight: true,
  },
  {
    id: "short_current",
    label: "Last",
    title: "Current Share Price",
    alignRight: true,
  },
  {
    id: "short_ch",
    label: "Chg",
    title: "Change in Price(Current Price-Last Price)",
    alignRight: true,
  },
  { id: "short_pL", label: "P(L)", title: "Profit/Loss", alignRight: true },
  {
    id: "short_chpercentage",
    label: "%Chg",
    title: "Percentage Change in Price",
    alignRight: true,
  },
  {
    id: "short_pl_avg_cost",
    label: "P(L) (Avg Cost)",
    title: "P(L) (Avg Cost) ",
    alignRight: true,
  },
  {
    id: "short_percentage_avg_cost",
    label: "% Chg (Avg Cost)",
    title: "% Chg (Avg Cost) ",
    alignRight: true,
  },

  {
    id: "short_cost",
    label: "Avg Cost",
    title: "Average Cost",
    alignRight: true,
  },
  {
    id: "short_tdg",
    label: "Tdg",
    title: "Profit/Loss from Trading Today",
    alignLeft: true,
  },
];
export const TABLE_HEAD_PORTFOLIO_LONG = [
  {
    id: "long_size",
    label: "Size",
    title: "Delta Adjusted Percent Of AUM",
    alignRight: true,
  },
  {
    id: "long_deltaadjustedsharecount",
    label: "Shares",
    title: "Delta Adjusted ShareCount",
    alignRight: true,
  },
  {
    id: "long_tickerSymbol",
    label: "Ticker",
    title: "Ticker Symbol",
    alignLeft: true,
  },
  {
    id: "long_last",
    label: "Yest",
    title: "Yesterday's Closing Share Price",
    alignRight: true,
  },
  {
    id: "long_current",
    label: "Last",
    title: "Current Share Price",
    alignRight: true,
  },
  {
    id: "long_ch",
    label: "Chg",
    title: "Change in Price(Current Price-Last Price)",
    alignRight: true,
  },
  { id: "long_pL", label: "P(L)", title: "Profit/Loss", alignRight: true },
  {
    id: "long_chpercentage",
    label: "%Chg",
    title: "Percentage Change in Price",
    alignRight: true,
  },
  {
    id: "long_pl_avg_cost",
    label: "P(L) (Avg Cost)",
    title: "P(L) (Avg Cost) ",
    alignRight: true,
  },
  {
    id: "long_percentage_avg_cost",
    label: "% Chg (Avg Cost)",
    title: "% Chg (Avg Cost) ",
    alignRight: true,
  },

  {
    id: "long_cost",
    label: "Avg Cost",
    title: "Average Cost",
    alignRight: true,
  },
  {
    id: "long_tdg",
    label: "Tdg",
    title: "Profit/Loss from Trading Today",
    alignLeft: true,
  },
];
export const TABLE_HEAD_SIGNAL = [
  { id: "tickers", label: "Ticker", alignCenter: true },
  { id: "weights", label: "Weighting %", alignCenter: true },
];
export const TABLE_HEAD_SIGNALS_CURRENT = [
  { id: "name", label: "Ticker", alignCenter: true },
  { id: "value", label: "Weighting %", alignCenter: true },
  { id: "long_short", label: "L/S", alignCenter: true },
  {
    id: "assetType",
    label: "Asset Type",
    alignCenter: true,
  },
  { id: "news", label: "News", alignCenter: true, widthIdentifier: true },
];

export const TABLE_HEAD_SIGNALS = [
  { id: "name", label: "Ticker", alignCenter: true },
  { id: "value", label: "Weighting %", alignCenter: true },
  { id: "long_short", label: "L/S", alignCenter: true },
  {
    id: "investment",
    label: "Asset Type",
    alignCenter: true,
  },
];

export const TABLE_HEAD_SIGNAL_HISTORICAL = [
  { id: "name", label: "Ticker", alignCenter: true },
  { id: "value", label: "Weighting %", alignCenter: true },
  { id: "long_short", label: "L/S", alignCenter: true },

];
export const TABLE_HEAD_SIGNALS_SUMMARIES = [
  { id: "name", label: "Ticker", alignCenter: true },
  { id: "perf", label: "Perf.", alignCenter: true },
  { id: "value", label: "Weighting %", alignCenter: true },
  { id: "long_short", label: "L/S", alignCenter: true, widthIdentifier: true },
];
export const TABLE_HEAD_SIGNALS_PERFORMANCE = [
  { id: "symbol", label: "Ticker", alignCenter: true },
  { id: "PercentChangeDay", label: "Intraday", alignCenter: true },
  { id: "PercentChangeWeek", label: "WTD", alignCenter: true },
  { id: "PercentChangeMonth", label: "MTD", alignCenter: true },
  { id: "PercentChangeQuarter", label: "QTD", alignCenter: true },
  { id: "marketCap", label: "Market Cap", alignCenter: true },
];
export const TABLE_HEAD_SIGNALS_PERFORMANCE_MARKETCAP = [
  { id: "myCap", label: "Market Cap", alignCenter: true },
  { id: "cut", label: "Cut", alignCenter: true },
  { id: "long", label: "Long", alignCenter: true },
  { id: "short", label: "Short", alignCenter: true },
  { id: "bias", label: "Bias", alignCenter: true },
];
export const TABLE_HEAD_SIGNALS_PERFORMANCE_SECTORCAP = [
  { id: "sector", label: "Sector", alignCenter: true },
  { id: "long", label: "Long", alignCenter: true },
  { id: "short", label: "Short", alignCenter: true },
  { id: "bias", label: "Bias", alignCenter: true },
];
export const TABLE_HEAD_SIGNALS_PERFORMANCE_SUMMARY = [
  { id: "position", label: "Position", alignCenter: true },
  { id: "PercentChangeDay", label: "Intraday", alignCenter: true },
  { id: "PercentChangeWeek", label: "WTD", alignCenter: true },
  { id: "PercentChangeMonth", label: "MTD", alignCenter: true },
  { id: "PercentChangeQuarter", label: "QTD", alignCenter: true },
];
export const TABLE_HEAD_EXISTING = [
  { id: "tickersExisting", label: "Ticker", alignCenter: true },
  { id: "weightsExisting", label: "Weighting", alignCenter: true },
  { id: "shares", label: "Trade Order Shares", alignCenter: true },
];
export const TABLE_HEAD_Locates = [
  { id: "tickerslocates", label: "Ticker", alignCenter: true },
  { id: "orginal", label: "Original Weighting", alignCenter: true },
  { id: "new", label: "New Weighting", alignCenter: true },
];
export const TABLE_HEAD_SIGNAL_LOCATES = [
  { id: "ticker", label: "Ticker", alignCenter: true },
  { id: "investment", label: "Security Type", alignCenter: true },
  { id: "price", label: "Market Price", alignCenter: true },
  { id: "marketData", label: "Market Data Source", alignCenter: true },
];
export const TABLE_HEAD_SWAP_STATUS = [
  { id: "name", label: "Swap", alignCenter: true },
  { id: "ticker", label: "Ticker", alignCenter: true },
  { id: "reason", label: "Reason", alignCenter: true },
];
export const TABLE_HEAD_SWAP_STATUS_SUMMARY = [
  { id: "name", label: "Swap", alignCenter: true },
  { id: "status", label: "Status", alignCenter: true },
  { id: "error", label: "Reason", alignCenter: true },
];
export const TABLE_HEAD_SWAP_REBALANCE_ITEMS = [
  { id: "name", label: "Name", alignCenter: true },
  { id: "executionStyle", label: "Execution Style", alignCenter: true },
  { id: "currentWeight", label: "Current Weight", alignCenter: true },
  { id: "newWeight", label: "New Weight", alignCenter: true },
];

export const TABLE_HEAD_ALLOC = [
  { id: "fund", label: "Fund", alignCenter: true },
  { id: "strategy", label: "Strategy", alignCenter: true },
  { id: "status", label: "Status", alignCenter: true },
  { id: "reason", label: "Reason", alignCenter: true },
];
export const TABLE_HEAD_FUND_REBALANCE_EXISTING_DATA = [
  { id: "ticker", label: "Ticker", alignCenter: true },
  { id: "signal", label: "Signal", alignCenter: true },
  { id: "CurrentWeight", label: "Weight", alignCenter: true },
  { id: "shareCountCurrent", label: "Shares", alignCenter: true },
  { id: "RequiredWeight", label: "Weight", alignCenter: true },
  { id: "shareCountRequired", label: "Shares", alignCenter: true },
];
export const TABLE_HEAD_FUND_REBALANCE_EXISTING_DATA_DELTA = [
  { id: "ticker", label: "Ticker", alignCenter: true },
  { id: "signal", label: "Signal", alignCenter: true },
  { id: "CurrentWeight", label: "Weight", alignCenter: true },
  { id: "shareCountCurrent", label: "Shares", alignCenter: true },
  { id: "deltaWeight", label: "Weight", alignCenter: true },
  { id: "deltaShares", label: "Shares", alignCenter: true },
  { id: "RequiredWeight", label: "Weight", alignCenter: true },
  { id: "shareCountRequired", label: "Shares", alignCenter: true },

];

export const TABLE_HEAD_UNCHANGED_STOCKS = [
  { id: "symbol", label: "Symbol", alignCenter: true },
  { id: "currentPosition", label: "Position", alignCenter: true },
  { id: "signal", label: "Signal", alignCenter: true },
  { id: "reason", label: "Reason", alignCenter: true },
];

export const TABLE_HEAD_FUND_SWAP_REBALANCE_DATA = [
  { id: "ticker", label: "Ticker", alignCenter: true },
  { id: "position", label: "Position", alignCenter: true },
  { id: "signal", label: "Signal", alignCenter: true },
  { id: "diff", label: "Diff", alignCenter: true },
  { id: "style", label: "Style", alignCenter: true },
  { id: "startTime", label: "Start Time", alignCenter: true },
  { id: "endTime", label: "End Time", alignCenter: true },
  { id: "participation", label: "Participation", alignCenter: true },
];
export const TABLE_HEAD_FUND_REBALANCE_EXISTING = [
  { id: "ticker", label: "Ticker", alignCenter: true },
  { id: "shareCount", label: "Share Count", alignCenter: true },
  { id: "fund", label: "Fund", alignCenter: true },
];
export const TABLE_HEAD_FUND_REBALANCE_EXISTING2 = [
  { id: "ticker", label: "Ticker", alignCenter: true },
  { id: "weight", label: "Weight", alignCenter: true },
  { id: "shareCount", label: "Share Count", alignCenter: true },
  { id: "type", label: "Trade Type", alignCenter: true },
  { id: "marketPrice", label: "Market Price", alignCenter: true },
];
export const TABLE_HEAD_FUND_REBALANCE_LOCATES = [
  { id: "ticker", label: "Ticker", alignCenter: true },
  { id: "shareCount", label: "Locates Required", alignCenter: true },
  { id: "available", label: "Locates Available", alignCenter: true },
  { id: "fund", label: "Fund", alignCenter: true },
];
export const TABLE_HEAD_FUND_REBALANCE_BLACKLIST = [
  { id: "fund", label: "Fund", alignCenter: true },
  { id: "strategy", label: "Strategy", alignCenter: true },
  { id: "ticker", label: "Ticker", alignCenter: true },
  { id: "reason", label: "Reason", alignCenter: true },
  { id: "direction", label: "Direction", alignCenter: true },
];
export const TABLE_HEAD_FINAL = [
  { id: "tickers", label: "Ticker", alignCenter: true },
  { id: "weights", label: "Target Weighting", alignCenter: true },
];
export const Ammended_List = [
  { id: "sno", label: "#", alignCenter: true },
  { id: "tickers", label: "Ticker", alignCenter: true },
  { id: "idealweights", label: "Ideal Weight", alignCenter: true },
  { id: "currentweight", label: "Current Weight", alignCenter: true },
  { id: "variance", label: "Variance", alignCenter: true },
  { id: "sharecounts", label: "ShareCount", alignCenter: true },
];
export const TABLE_HEAD_LOT = [
  { id: "date", label: "Date", alignRight: false },
  { id: "strategy", label: "Strategy", alignRight: false },
  { id: "custodian", label: "Custodian", alignRight: false },
  { id: "shareCount", label: "Share Count", alignRight: true },
  { id: "costPerShare", label: "Cost Per Share", alignRight: true },
  { id: "sharePrice", label: "Share Price", alignRight: true },
  { id: "position", label: "Position", alignRight: true },
];
export const TABLE_HEAD_BOXED_LOT = [
  { id: "date", label: "Date", alignRight: false },
  { id: "strategy", label: "Strategy", alignRight: false },
  { id: "custodian", label: "Custodian", alignRight: false },
  { id: "cost", label: "Cost", alignRight: true },
  { id: "shareCount", label: "Share Count", alignRight: true },
  { id: "sharePrice", label: "Share Price", alignRight: true },
  { id: "value", label: "Value", alignRight: true },
  { id: "position", label: "Position", alignRight: true },
];
export const TABLE_HEAD_VANTAGE = [
  { id: "fundName", label: "Fund Name", alignCenter: true },
  { id: "strategyName", label: "Strategy Name", alignCenter: true },
  { id: "aum", label: "AUM", alignCenter: true },
  { id: "pL", label: "P(L)Today", alignCenter: true },
  { id: "plLong", label: "P(L)Today Long", alignCenter: true },
  { id: "plShort", label: "P(L)Today Short", alignCenter: true },
  {
    id: "sizeLong",
    label: "% AUM DA Long",
    alignCenter: true,
  },
  {
    id: "sizeShort",
    label: " % AUM DA Short",
    alignCenter: true,
  },
  {
    id: "netAumPercent",
    label: "Net AUM %",
    alignCenter: true,
  },
  { id: "longCapital", label: "Long Capital", alignCenter: true },
  { id: "shortCapital", label: "Short Capital", alignCenter: true },
  {
    id: "netCapital",
    label: "Net Capital",
    alignCenter: true,
  },
  {
    id: "longPerformance",
    label: "Long Performance",
    alignCenter: true,
  },
  {
    id: "shortPerformance",
    label: "Short Performance",
    alignCenter: true,
  },
  { id: "pLYest", label: "P(L)% Yest", alignCenter: true },
  { id: "plPer", label: "P(L)% Today", alignCenter: true },

  { id: "pLMonth", label: "P(L) Month", alignCenter: true },
  { id: "pLQuarter", label: "P(L) Quarter", alignCenter: true },
  { id: "pLYear", label: "P(L) Year", alignCenter: true },
  { id: "pLITD", label: "P(L) ITD", alignCenter: true },
];
export const TABLE_HEAD_FUNDPNL = [
  { id: "icon", label: "", alignCenter: true },
  { id: "fundName", label: "Fund Name", alignCenter: true },
  { id: "dailyPL", label: "$ Daily P&L", alignCenter: true },
  { id: "dailyPL%", label: "$ Daily P&L %", alignCenter: true },
  { id: "longDelta", label: "Long Delta %", alignCenter: true },
  { id: "shortDelta", label: "Short Delta %", alignCenter: true },
  { id: "netDelta", label: "Net Delta %", alignCenter: true },
];
export const TABLE_HEAD_LONGSHORT = [
  { id: "icon", label: "", alignCenter: true },
  { id: "netDelta", label: "$ Net", alignCenter: true },
  { id: "gross", label: "$ Gross", alignCenter: true },
  { id: "grosslong", label: " $Long", alignCenter: true },
  { id: "shortDelta", label: "$ Short", alignCenter: true },
  { id: "longDelta", label: "% AUM ", alignCenter: true },
  { id: "longDeltaTodays", label: "Today's Trades", alignCenter: true },
  { id: "options", label: "Options", alignCenter: true },
  { id: "daily", label: "$ Daily PnL", alignCenter: true },
  { id: "daily", label: "Daily Return", alignCenter: true },
];
export const TABLE_HEAD_COLLAPSED = [
  { id: "asset", label: "Asset", alignCenter: true },
  { id: "purchaseDate", label: "Purchase Date", alignCenter: true },
  { id: "daysHeld", label: "Days Held", alignCenter: true },
  { id: "longShort", label: "Long/Short", alignCenter: true },
  { id: "position", label: "Position", alignCenter: true },
  { id: "grossPrice", label: "Gross Price", alignCenter: true },
  { id: "marketValue", label: "Market Value", alignCenter: true },
  { id: "profitLoss", label: "Profit (loss)", alignCenter: true },
  { id: "emp1", label: "-", alignCenter: true },
  { id: "emp2", label: "-", alignCenter: true },
  { id: "emp3", label: "-", alignCenter: true },
];
export const TABLE_HEAD_COLLAPSED2 = [
  { id: "tickSym", label: "Ticker Symbol", alignCenter: true },
  { id: "quant", label: "Quantity", alignCenter: true },
  { id: "lp", label: "Last Price", alignCenter: true },
  { id: "pl", label: "Profit/(Loss)", alignCenter: true },
  { id: "expr", label: "$ Exposure", alignCenter: true },
  { id: "deltaexpr", label: "% Exposure", alignCenter: true },
];

export const Unallowed_List = [
  { id: "ticker", label: "Ticker", alignCenter: true },
  { id: "weightings", label: "Weight", alignCenter: true },
  { id: "sharecount", label: "Share Count", alignCenter: true },
  { id: "assignedto", label: "Assigned To", alignCenter: true },
];

export const Black_List = [
  { id: "ticker", label: "Ticker", alignCenter: true },
  { id: "weightings", label: "Weight", alignCenter: true },
];
export const strategy_List = [
  { id: "strategy", label: "Strategies", alignCenter: true },
];
export const pereference_List = [
  { id: "broker_preference", label: "Broker Preference", alignCenter: true },
  {
    id: "custodian_preference",
    label: "Custodian Preference",
    alignCenter: true,
  },
];
export const Existing_List = [
  { id: "allocation", label: "Allocation to Strategy", alignCenter: true },
  { id: "percentLong", label: "Percent Long", alignCenter: true },
  { id: "percentShort", label: "Percent Short", alignCenter: true },
];
export const Existing_List_Target = [
  { id: "existing", label: "Allocation to Strategy", alignLeft: true },
  { id: "allocation", label: "Allocation to Strategy", alignLeft: true },
  { id: "plong", label: "Percent Long", alignLeft: true },
  { id: "pshort", label: "Percent Short", alignLeft: true },
];
export const Fund_aum_List = [
  { id: "totalfund", label: "Total for Fund", alignCenter: true },
  { id: "Epercent", label: "Percent", alignCenter: true },
  { id: "Eamount", label: "Amount", alignCenter: true },
  { id: "Tpercent", label: "Percent", alignCenter: true },
  { id: "Tamount", label: "Amount", alignCenter: true },
];

export const Signal_List = [
  { id: "newsignal", label: "New Signals List", alignCenter: true },
  { id: "addsignal", label: "Add Signal", alignCenter: true },
];

export const New_Trade_Order = [
  { id: "ticker", label: "Ticker", alignCenter: true },
  { id: "tradetype", label: "Trade Type", alignCenter: true },
  { id: "sharesorder", label: "Shares Ordered", alignCenter: true },
  { id: "shareexecuted", label: "Share Executed", alignCenter: true },
  { id: "brokerid", label: "Broker Id", alignCenter: true },
  { id: "primebroker", label: "Prime Broker", alignCenter: true },
  { id: "investmenttype", label: "Investment Type", alignCenter: true },
];
export function inputChecker(string) {
  let ticker = {
    symbol: "",
    currency: "USD",
    country: "US",
    investment: "equity",
  };
  let isValid = true;
  const resultArray = string.split(/\s+/);
  const lastItem = resultArray[resultArray.length - 1];
  const isBloomberg =
    lastItem.toLowerCase() === "options" || lastItem.toLowerCase() === "equity";

  if (isBloomberg) {
    ticker.investment = lastItem.toLowerCase();
    const secondLastItem = resultArray[resultArray.length - 2];
    const isValidCountryCode = /^[a-zA-Z]{2}$/.test(secondLastItem);
    if (isValidCountryCode) {
      ticker.country = secondLastItem;
      const firstItem = resultArray[0];
      const thirdLastItem = resultArray[resultArray.length - 3];
      if (firstItem != thirdLastItem) {
        isValid = false;
        ticker.symbol = firstItem;
      } else {
        if (/^[a-zA-Z0-9]+$/.test(firstItem)) {
          ticker.symbol = firstItem;
        } else if (/[/]/.test(firstItem)) {
          const [symbol, stock] = firstItem.split(/[/]/);
          // Check if stock has exactly one character after slash
          if (stock && stock.length === 1 && /^[a-zA-Z0-9]$/.test(stock)) {
            ticker.stock = stock;
            ticker.symbol = symbol;
          } else {
            isValid = false;
          }
        } else {
          isValid = false;
        }
      }
    } else {
      isValid = false;
    }
  } else {
    if (resultArray.length == 1) {
      if (/[./]/.test(lastItem)) {
        const [symbol, stock] = lastItem.split(/[./]/);
        // Check if stock has exactly one character after slash
        if (stock && stock.length === 1 && /^[a-zA-Z0-9]$/.test(stock)) {
          ticker.symbol = symbol;
          ticker.stock = stock;
        } else {
          isValid = false;
        }
      } else if (/^[a-zA-Z0-9]+$/.test(lastItem)) {
        ticker.symbol = lastItem;
      } else {
        isValid = false;
      }
    } else {
      if (/^[a-zA-Z]$/.test(lastItem)) {
        ticker.stock = lastItem;
      } else {
        isValid = false;
      }
    }
  }

  return { ticker, isValid };
}
export const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
};

export const a11yProps = (index) => {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
};

export const Tablecell = styled(TableCell)(({ theme }) => ({
  color: "#000",
  fontSize: "12px",
  padding: "0px",
  border: "0.1px solid #dadada",
}));
const TablecellPerformance = styled(TableCell)(({ theme }) => ({
  fontSize: "12px",
  padding: "1px 2px 1px 5px",
}));

export const BoxHold = styled(Box)(({ theme }) => ({
  width: "100%",
  background: "rgb(242 242 242)",
  borderRadius: 4,
  display: "flex",
  flexDirection: "column",
  padding: "5px",
  margin: "5px",
}));
export const StyledRoot = styled(Toolbar)(({ theme }) => ({
  height: 50,
  display: "flex",
  justifyContent: "space-between",
}));
export const StyledTab = styled(Tab)(({ theme }) => ({
  fontSize: "12px",
  color: theme.palette.primary.light,
  margin: 2,
}));

export const StyledSearch = styled(OutlinedInput)(({ theme }) => ({
  width: 300,
  height: 40,
  borderRadius: 3,
  background: "#FFF",
  fontSize: "15px",
  transition: theme.transitions.create(["box-shadow", "width"], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
  "&.Mui-focused": {
    boxShadow:
      "0 0 2px 0 rgb(145 158 171 / 20%), 0 12px 24px -4px rgb(145 158 171 / 12%)",
  },
  "& fieldset": {
    borderWidth: `1px !important`,
    borderColor: "#A9A9A9",
  },
}));
export const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#5A5A5A",
    color: "#fff",
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: "1px solid #dadde9",
  },
}));
export function isDateOlderThan5Minutes(dateString, threshold) {
  if (dateString === undefined || threshold === undefined) {
    return true;
  }

  const currentDate = new Date();
  const givenDate = new Date(dateString);
  const timeDifference = currentDate - givenDate;
  const minutesDifference = timeDifference / (1000 * 60);
  return minutesDifference > threshold;
}

export const processData = (strategies, strategyValObj) => {
  if (strategyValObj && strategies) {
    const strategy = strategies.find(
      (strategy) => strategy.strategyId === strategyValObj
    );

    if (strategy && strategy.signals) {
      const { weights_keys } = strategy.signals;
      const keys = Object.keys(weights_keys);

      if (keys.length > 0) {
        const tickers = keys.map((key) => ({
          symbol: weights_keys[key]?.symbol,
          currency: weights_keys[key]?.currency,
          investment: weights_keys[key]?.investment,
          exchange: weights_keys[key]?.exchange,
        }));

        try {
          sendWebSocketMessage(
            JSON.stringify({
              action: "market_data",
              data: {
                threshold: 2592000,
                request: true,
                fields: [4, 5, 6, 7, 8, 11],
                tickers: tickers,
              },
            })
          );
        } catch (error) {
          console.error("Error sending WebSocket message:", error);
        }
      }
    }
  }
};

export const fetchTodaysSymbols = (trades) => {
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  const endOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    23,
    59,
    59,
    999
  );

  const startOfTodayTimestamp = startOfToday.getTime();
  const endOfTodayTimestamp = endOfToday.getTime();

  let todaysTrades = trades.filter((trade) => {
    const lastOrderDate = trade.orders[trade.orders.length - 1].date;
    return (
      lastOrderDate >= startOfTodayTimestamp &&
      lastOrderDate <= endOfTodayTimestamp
    );
  });

  let uniqueSymbols = new Set();

  todaysTrades.map((trade) => {
    const lastOrder = trade.orders[trade.orders.length - 1];
    const symbol = lastOrder.ticker.symbol.toLowerCase();

    if (!uniqueSymbols.has(symbol)) {
      uniqueSymbols.add(symbol);
    }
  });

  const result = Array.from(uniqueSymbols).map((symbol) => {
    return {
      investment: 4,
      symbol: symbol,
      currency: 7,
      exchange: 4,
    };
  });

  return result;
};

const tradeDistributionCache = new WeakMap();

const getTradeBaseDistribution = (trade) => {
  const cached = tradeDistributionCache.get(trade);
  if (cached !== undefined) {
    return cached;
  }

  const lastOrder = trade?.orders[trade?.orders?.length - 1];
  const symbol = lastOrder?.ticker?.symbol.toLowerCase();
  const broker = lastOrder?.broker;

  const distribute = Object.entries(lastOrder?.distribution || {}).reduce(
    (acc, [key, value]) => {
      const parts = key.split("#");
      const [account, fund] = parts;
      const strategy = parts.length >= 4 ? parts[3] : "";

      if (!acc[fund]) {
        acc[fund] = [];
      }

      acc[fund].push({
        shareCount: Number(value),
        account: account,
        strategy: strategy
      });

      return acc;
    },
    {}
  );

  const base = {
    symbol,
    broker,
    action: lastOrder.action,
    position: lastOrder.position,
    distribute
  };

  tradeDistributionCache.set(trade, base);
  return base;
};

/** Read a usable share price from a fill/manual-like object. */
const readSharePrice = (candidate) => {
  if (candidate == null || typeof candidate !== "object") return null;
  const raw =
    candidate.sharePrice ??
    candidate.price ??
    candidate.avgPrice ??
    candidate.averagePrice ??
    candidate.fillPrice ??
    candidate.pricePerShare;
  if (raw == null || raw === "") return null;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed === 0) return null;
  return parsed;
};

/** Prefer fill or manual entry that actually has a usable sharePrice. */
const resolveFillPriceSource = (trade, debugLabel) => {
  const candidates = [
    trade?.fill,
    ...(trade?.manuals?.length ? [trade.manuals.slice(-1)[0]] : []),
  ].filter(Boolean);

  const withPrice = candidates
    .map((candidate) => ({
      candidate,
      price: readSharePrice(candidate),
    }))
    .filter((entry) => entry.price != null);

  const resolved = withPrice[0] || null;

  if (debugLabel) {
    const fill = trade?.fill;
    console.log(`[${debugLabel}] resolveFillPriceSource`, {
      tradeId: trade?.tradeId,
      hasFill: fill != null,
      fillType: typeof fill,
      fillIsArray: Array.isArray(fill),
      fillKeys: fill && typeof fill === "object" ? Object.keys(fill) : null,
      fillEntries:
        fill && typeof fill === "object"
          ? Object.fromEntries(
            Object.keys(fill).map((key) => [key, fill[key]])
          )
          : null,
      fillJSON: (() => {
        try {
          return JSON.stringify(fill);
        } catch (e) {
          return String(e);
        }
      })(),
      fillSharePriceDirect: fill?.sharePrice,
      readSharePriceFromFill: readSharePrice(fill),
      manualsLength: trade?.manuals?.length || 0,
      lastManual: trade?.manuals?.length ? trade.manuals.slice(-1)[0] : null,
      withPriceCount: withPrice.length,
      resolvedPrice: resolved?.price ?? null,
      resolvedCandidate: resolved?.candidate ?? null,
    });
  }

  return resolved;
};

const parseSharePrice = (value) => {
  const parsed = parseFloat(Number(value).toFixed(4));
  return Number.isFinite(parsed) && parsed !== 0 ? parsed : null;
};

export const calculateTdg = (trades, marketData, fund, strategy, filled = true) => {
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  const endOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    23,
    59,
    59,
    999
  );

  const startOfTodayTimestamp = startOfToday.getTime();
  const endOfTodayTimestamp = endOfToday.getTime();

  let todaysTrades = trades.filter((trade) => {
    const lastOrderDate = trade.orders[trade.orders.length - 1].date;
    return (
      lastOrderDate >= startOfTodayTimestamp &&
      lastOrderDate <= endOfTodayTimestamp
    );
  });



  const rawTrades = todaysTrades
    .map((trade) => {
      const { symbol, broker, action, position, distribute } = getTradeBaseDistribution(trade);


      let completedShares, fillPrice;

      let distribution = distribute;

      //If there is no distribution, skip it ---
      const distributionKeys = Object.keys(distribution);
      if (distributionKeys.length === 0) {
        return null;
      }

      //If market data is not found, skip it ---
      const marketInfo = marketData.find(
        (item) => item.symbol.toLowerCase() === symbol.toLowerCase()
      );
      if (!marketInfo) {
        return null;
      }

      if (fund) {
        const keys = Object.keys(distribution);
        const fundKeys = keys.filter((key) => key === fund);

        if (fundKeys.length > 0) {
          distribution = distribution[fundKeys[0]];
        } else {
          return null;
        }
      } else {
        return null;
      }

      if (strategy) {
        distribution = distribution.filter((item) => item.strategy === strategy);
        if (distribution.length === 0) {
          return null;
        }
      }

      distribution = distribution.filter(
        (item) => !isIgnoredMonitorStrategy(item.strategy)
      );
      if (distribution.length === 0) {
        return null;
      }

      const totalShareCount = distribution.reduce((sum, item) => {
        return sum + item.shareCount;
      }, 0);

      const priceSource = resolveFillPriceSource(
        trade, null
      );

      const parsedFillPrice = priceSource
        ? parseSharePrice(priceSource.price)
        : null;


      const hasFillEvidence =
        !!trade.fill || (trade.manuals && trade.manuals.length > 0);

      if (filled) { //If filled trades are fetched
        if (parsedFillPrice != null) {
          // completedShares = parseFloat(trade.fill.shareCount.toFixed(4));  //Removed Temporarily
          completedShares = totalShareCount; //TODO: Send prorata filled shares
          fillPrice = parsedFillPrice;
        } else if (broker == "5b676cc69b3fe572df5032c52b8496ef") { //Then we will check if the broker is cross broker
          completedShares = totalShareCount;
          fillPrice = marketInfo.price;
        } else if (hasFillEvidence) {
          // Fill exists but price missing — do not treat as $0
          return null;
        } else {
          return null;
        }
      } else { // If All Trades are fetched
        completedShares = totalShareCount;
        if (parsedFillPrice != null) {
          fillPrice = parsedFillPrice;
        } else {
          fillPrice = marketInfo.price;
        }
      }

      const timeAgo = formatTimeAgo(marketInfo.expiration);
      // Magnitudes only — direction comes from action/position
      const shares = Math.abs(Number(completedShares) || 0);
      if (shares === 0) {
        return null;
      }
      const normalizedAction = Number(action);
      const normalizedPosition = Number(position);
      const totalCost = shares * fillPrice;

      return {
        symbol,
        action: normalizedAction,
        position: normalizedPosition,
        completedShares: shares,
        fillPrice,
        totalCost,
        currentMarketPrice: marketInfo.price,
        yestPrice: marketInfo.yestPrice,
        valid: timeAgo.valid,
        fetched: timeAgo.msg,
      };
    })
    .filter((trade) => trade !== null);

  // One row per symbol + action + position (preserves trade type; VWAP fill).
  // Do not net buy vs sell — TDG must use each leg's fill vs market/avg cost.
  const aggregatedByType = rawTrades.reduce((acc, trade) => {
    const key = `${trade.symbol}|${trade.action}|${trade.position}`;
    const shares = trade.completedShares;

    if (acc[key]) {
      const prev = acc[key];
      const totalShares = prev.completedShares + shares;
      prev.fillPrice =
        totalShares > 0
          ? (prev.fillPrice * prev.completedShares + trade.fillPrice * shares) /
          totalShares
          : prev.fillPrice;
      prev.completedShares = totalShares;
      prev.totalCost = prev.completedShares * prev.fillPrice;
    } else {
      acc[key] = { ...trade };
    }
    return acc;
  }, {});

  return Object.values(aggregatedByType).map((trade) => ({
    symbol: trade.symbol,
    position: trade.position,
    action: trade.action,
    completedShares: trade.completedShares,
    fillPrice: trade.fillPrice,
    totalCost: trade.totalCost,
    currentMarketPrice: trade.currentMarketPrice,
    yestPrice: trade.yestPrice,
    valid: trade.valid,
    fetched: trade.fetched,
    dataSource: "Primary",
  })).filter((trade) => {
    return true;
  });
};

/**
 */
export const normalizeTickerForComparison = (ticker) => {
  if (!ticker) return "";

  const trimmed = ticker.trim().toUpperCase();

  // Split by either '.' or '/'
  const parts = trimmed.split(/[./]/);

  if (parts.length === 1) {
    // No separator, just return the ticker
    return parts[0];
  } else if (parts.length === 2) {
    // Has separator, normalize to use '.' as standard
    return `${parts[0]}.${parts[1]}`;
  } else {
    // Multiple separators, just use the first two parts
    return `${parts[0]}.${parts[1]}`;
  }
};

export const signalsData = (signalsPerformance, strategies, strategyValObj) => {
  if (signalsPerformance && signalsPerformance.length > 0) {
    if (strategyValObj && strategies) {
      const strategy = strategies.find(
        (strategy) => strategy.strategyId === strategyValObj
      );

      if (strategy && strategy.signals) {
        const { weights_keys, weights_values } = strategy.signals;
        const keys = Object.keys(weights_keys);

        if (keys.length > 0) {
          const combinedArray = keys.map((key) => ({
            key: key,
            symbol: weights_keys[key]?.symbol,
            value: weights_values[key] * 100,
          }));

          const combinedSymbols = combinedArray.map((item) => item.symbol);

          return signalsPerformance
            .filter((item) => combinedSymbols.includes(item.symbol))
            .map((item) => {
              const combinedItem = combinedArray.find(
                (combined) => combined.symbol === item.symbol
              );

              return {
                notRecent: isDateOlderThan5Minutes(item?.expiration, 120),
                PercentChangeDay: parseFloat(item?.PercentChangeDay),
                PercentChangeMonth: parseFloat(item?.PercentChangeMonth),
                PercentChangeQuarter: parseFloat(item?.PercentChangeQuarter),
                PercentChangeWeek: parseFloat(item?.PercentChangeWeek),
                marketCap: parseFloat(item?.marketCap),
                symbol: item?.symbol,
                expiration: item?.expiration,
                sector: item?.sector,
                value: parseFloat(combinedItem?.value),
              };
            });
        }
      }
    }
  }
  return [];
};
